/**
 * Live data (docs/LIVE-DATA.md): every screen shows what is true now, without
 * a reload. Three freshness tiers, and one way for a change made in one place
 * to show up everywhere.
 *
 * - **records** — the query client's default (src/adapters/rpc/query-client.ts):
 *   asked again on window focus and reconnect once older than
 *   `RECORDS_STALE_MS`, and invalidated by every mutation that touches it.
 * - **status** — health, alerts, deliveries: `statusQuery(...)` polls while the
 *   tab is visible. Register the view in `STATUS_VIEWS` (src/features/shared/lib/live-data-registry.ts);
 *   a test fails if its query has no refetch interval.
 * - **reference** — the dictionary and labels: `REFERENCE_STALE_MS`, and
 *   invalidated on every edit, so a rename still shows at once.
 *
 * The cross-screen pattern is the **touch map**: a mutation names the kinds
 * of record it touches (`useInvalidate('items')`), and the map names
 * every query that shows them. A new screen that shows items adds its
 * key to the map once; every mutation that touches items then
 * refreshes it, with no mutation changed.
 */
import type { QueryClient, QueryKey } from '@tanstack/react-query'

/** Records tier: how long a fetched answer counts as fresh before focus or reconnect asks again. */
export const RECORDS_STALE_MS = 15_000

/** Status tier: how often a visible status view asks again (docs/LIVE-DATA.md, 15–30 s). */
export const STATUS_REFRESH_MS = 20_000

/** Reference tier: rarely changes, and every edit invalidates it anyway. */
export const REFERENCE_STALE_MS = 5 * 60_000

/**
 * A status view's query options: polled every `every` ms while the tab is
 * visible (never in a background tab — that is all cost and no reader), and
 * always asked again on focus. Wrap the options, not the hook:
 * `useQuery(statusQuery(orpc.users.list.queryOptions()))`.
 */
export function statusQuery<T extends object>(options: T, every: number = STATUS_REFRESH_MS) {
  return {
    ...options,
    staleTime: 0,
    refetchInterval: every,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  } as const
}

/** Which query keys show each kind of record — the project's touch map (src/features/shared/lib/live-data-registry.ts). */
export type TouchMap<Kind extends string> = Record<Kind, () => readonly QueryKey[]>

/** Invalidates every query that shows any of `kinds`; active ones are fetched again at once. */
export function invalidateTouched<Kind extends string>(
  queryClient: QueryClient,
  map: TouchMap<Kind>,
  kinds: readonly Kind[],
): Promise<void> {
  const keys = kinds.flatMap((kind) => map[kind]())
  return Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))).then(
    () => undefined,
  )
}
